import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'wouter';
import { MessageSquare, Plus, Trash2, Pencil, Lock, UserCheck, ChevronDown, ChevronRight, Send, Check, Globe, Users } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Modal } from '../components/Modal';
import { api } from '../lib/api';
import { useAuth } from '../hooks/use-auth';

type Visibility = 'private' | 'band' | 'selected';

const VISIBILITY_CONFIG = {
  private: { label: 'Private', icon: Lock, color: 'text-gray-400', bg: 'bg-gray-600/20' },
  band: { label: 'Shared with Band', icon: Globe, color: 'text-primary-400', bg: 'bg-primary-600/20' },
  selected: { label: 'Selected Members', icon: UserCheck, color: 'text-accent-400', bg: 'bg-accent-600/20' },
};

export function RehearsalNotesPage() {
  const params = useParams<{ bandId: string }>();
  const bandId = Number(params.bandId);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [expandedNoteId, setExpandedNoteId] = useState<number | null>(null);

  const { data: band } = useQuery({
    queryKey: ['band', bandId],
    queryFn: () => api.bands.get(bandId),
  });

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ['rehearsalNotes', bandId],
    queryFn: () => api.rehearsalNotes.listForBand(bandId),
  });

  return (
    <Layout bandId={bandId} bandName={band?.name} title="Rehearsal Notes">
      <div className="p-4 md:p-6 max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary-400" />
              Rehearsal Notes
            </h2>
            <p className="text-sm text-gray-500 mt-1">Session notes, ideas, and rehearsal logs for the band.</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            New Note
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : notes.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 mb-2">No rehearsal notes yet.</p>
            <p className="text-sm text-gray-500">Create a note to start capturing rehearsal ideas, feedback, and session logs.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((note: any) => (
              <NoteItem
                key={note.id}
                note={note}
                isExpanded={expandedNoteId === note.id}
                onToggle={() => setExpandedNoteId(expandedNoteId === note.id ? null : note.id)}
                bandId={bandId}
                currentUserId={user?.id}
              />
            ))}
          </div>
        )}

        <CreateNoteModal
          open={showCreate}
          onClose={() => setShowCreate(false)}
          bandId={bandId}
        />
      </div>
    </Layout>
  );
}

function NoteItem({ note, isExpanded, onToggle, bandId, currentUserId }: {
  note: any;
  isExpanded: boolean;
  onToggle: () => void;
  bandId: number;
  currentUserId?: string;
}) {
  const queryClient = useQueryClient();
  const isOwner = note.createdByUserId === currentUserId;
  const vis = VISIBILITY_CONFIG[note.visibility as Visibility];
  const VisIcon = vis.icon;

  const { data: noteDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['rehearsalNote', note.id],
    queryFn: () => api.rehearsalNotes.get(note.id),
    enabled: isExpanded,
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.rehearsalNotes.delete(note.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rehearsalNotes', bandId] });
    },
  });

  const [showEdit, setShowEdit] = useState(false);
  const [showManageEditors, setShowManageEditors] = useState(false);
  const permissionSource = noteDetail || note;
  const canEdit = permissionSource.canEdit ?? isOwner;
  const canDelete = permissionSource.canDelete ?? isOwner;
  const canManageEditors = permissionSource.canManageEditors ?? isOwner;

  return (
    <div className="bg-surface-900 border border-surface-700 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-surface-800/50 transition-colors text-left"
      >
        {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{note.title}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {note.creatorName} &middot; {new Date(note.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1 text-xs ${vis.bg} ${vis.color} px-2 py-0.5 rounded-full`}>
          <VisIcon className="w-3 h-3" />
          {vis.label}
        </span>
        <span className="text-xs text-gray-500 shrink-0">{note.entryCount} {note.entryCount === 1 ? 'entry' : 'entries'}</span>
      </button>

      {isExpanded && (
        <div className="border-t border-surface-700">
          {(canEdit || canDelete || canManageEditors) && (
            <div className="flex flex-wrap items-center gap-1 px-3 py-1.5 border-b border-surface-800 bg-surface-950/50">
              {canEdit && (
                <button
                  onClick={() => setShowEdit(true)}
                  className="min-h-9 flex items-center gap-1.5 px-2 text-xs text-gray-400 hover:text-gray-200 hover:bg-surface-800 rounded-md transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Edit
                </button>
              )}
              {canManageEditors && (
                <button
                  onClick={() => setShowManageEditors(true)}
                  className="min-h-9 flex items-center gap-1.5 px-2 text-xs text-gray-400 hover:text-gray-200 hover:bg-surface-800 rounded-md transition-colors"
                >
                  <Users className="w-3.5 h-3.5" />
                  Manage Editors
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => { if (confirm('Delete this rehearsal note and all its entries?')) deleteMutation.mutate(); }}
                  disabled={deleteMutation.isPending}
                  className="min-h-9 flex items-center gap-1.5 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 disabled:opacity-50 rounded-md transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              )}
            </div>
          )}

          {detailLoading ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500">Loading...</div>
          ) : noteDetail ? (
            <>
              {noteDetail.editors?.length > 0 && (
                <div className="px-4 py-2 border-b border-surface-800 text-xs text-gray-500 flex items-start gap-1.5">
                  <Users className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span className="min-w-0 break-words">
                    Editors: {formatEditorNames(noteDetail.editors)}
                  </span>
                </div>
              )}
              <NoteEntries noteDetail={noteDetail} bandId={bandId} />
            </>
          ) : null}

          {showEdit && (
            <EditNoteModal
              open={showEdit}
              onClose={() => setShowEdit(false)}
              note={noteDetail || note}
              bandId={bandId}
              canManageAccess={canManageEditors}
            />
          )}

          {showManageEditors && noteDetail && (
            <ManageEditorsModal
              open={showManageEditors}
              onClose={() => setShowManageEditors(false)}
              note={noteDetail}
              bandId={bandId}
            />
          )}
        </div>
      )}
    </div>
  );
}

function formatEditorNames(editors: any[]) {
  const names = editors.map(editor => editor.name);
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
}

function NoteEntries({ noteDetail, bandId }: { noteDetail: any; bandId: number }) {
  const queryClient = useQueryClient();
  const [newEntry, setNewEntry] = useState('');
  const [editingEntryId, setEditingEntryId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');

  const addEntry = useMutation({
    mutationFn: (content: string) => api.rehearsalNotes.addEntry(noteDetail.id, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rehearsalNote', noteDetail.id] });
      queryClient.invalidateQueries({ queryKey: ['rehearsalNotes', bandId] });
      setNewEntry('');
    },
  });

  const updateEntry = useMutation({
    mutationFn: ({ entryId, content }: { entryId: number; content: string }) =>
      api.rehearsalNotes.updateEntry(noteDetail.id, entryId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rehearsalNote', noteDetail.id] });
      setEditingEntryId(null);
    },
  });

  const deleteEntry = useMutation({
    mutationFn: (entryId: number) => api.rehearsalNotes.deleteEntry(noteDetail.id, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rehearsalNote', noteDetail.id] });
      queryClient.invalidateQueries({ queryKey: ['rehearsalNotes', bandId] });
    },
  });

  return (
    <div>
      {noteDetail.entries.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-gray-500">No entries yet. Add the first one below.</div>
      ) : (
        <div className="divide-y divide-surface-800">
          {noteDetail.entries.map((entry: any) => (
            <div key={entry.id} className="px-4 py-3">
              {editingEntryId === entry.id ? (
                <div className="space-y-2">
                  <textarea
                    value={editContent}
                    onChange={e => setEditContent(e.target.value)}
                    className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                    rows={3}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateEntry.mutate({ entryId: entry.id, content: editContent })}
                      disabled={updateEntry.isPending || !editContent.trim()}
                      className="flex items-center gap-1 text-xs bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white py-1 px-2.5 rounded transition-colors"
                    >
                      <Check className="w-3 h-3" />
                      Save
                    </button>
                    <button onClick={() => setEditingEntryId(null)} className="text-xs text-gray-400 hover:text-gray-200 py-1 px-2">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs font-medium text-gray-300">{entry.authorName}</span>
                    <span className="text-xs text-gray-600">
                      {new Date(entry.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      {' '}
                      {new Date(entry.createdAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {entry.isAuthor && (
                      <div className="flex gap-1 ml-auto">
                        <button
                          onClick={() => { setEditingEntryId(entry.id); setEditContent(entry.content); }}
                          className="p-0.5 text-gray-500 hover:text-gray-300 transition-colors"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => { if (confirm('Delete this entry?')) deleteEntry.mutate(entry.id); }}
                          className="p-0.5 text-gray-500 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">{entry.content}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {noteDetail.canWrite && (
        <div className="border-t border-surface-700 px-4 py-3 bg-surface-950/30">
          <div className="flex gap-2">
            <textarea
              value={newEntry}
              onChange={e => setNewEntry(e.target.value)}
              placeholder="Add an entry..."
              className="flex-1 bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              rows={2}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && newEntry.trim()) {
                  addEntry.mutate(newEntry);
                }
              }}
            />
            <button
              onClick={() => newEntry.trim() && addEntry.mutate(newEntry)}
              disabled={addEntry.isPending || !newEntry.trim()}
              className="self-end p-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-gray-600 mt-1">Ctrl+Enter to send</p>
        </div>
      )}
    </div>
  );
}

function CreateNoteModal({ open, onClose, bandId }: {
  open: boolean;
  onClose: () => void;
  bandId: number;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('private');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const { data: members = [] } = useQuery({
    queryKey: ['bandMembers', bandId],
    queryFn: () => api.bands.getMembers(bandId),
    enabled: open,
  });

  const { user } = useAuth();

  const createMutation = useMutation({
    mutationFn: () => api.rehearsalNotes.create({
      bandId,
      title,
      visibility,
      selectedUserIds: visibility === 'selected' ? selectedUserIds : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rehearsalNotes', bandId] });
      setTitle('');
      setVisibility('private');
      setSelectedUserIds([]);
      onClose();
    },
  });

  const otherMembers = members.filter((m: any) => m.userId !== user?.id);

  return (
    <Modal open={open} onClose={onClose} title="New Rehearsal Note">
      <form onSubmit={e => { e.preventDefault(); createMutation.mutate(); }} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g., Rehearsal 3/9 Notes, Set 2 Ideas"
            className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Visibility</label>
          <div className="space-y-2">
            {(Object.entries(VISIBILITY_CONFIG) as [Visibility, typeof VISIBILITY_CONFIG.private][]).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <label
                  key={key}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    visibility === key
                      ? 'border-primary-500 bg-primary-600/10'
                      : 'border-surface-700 hover:border-surface-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="visibility"
                    value={key}
                    checked={visibility === key}
                    onChange={() => setVisibility(key)}
                    className="sr-only"
                  />
                  <Icon className={`w-4 h-4 ${config.color}`} />
                  <span className="text-sm">{config.label}</span>
                </label>
              );
            })}
          </div>
        </div>

        {visibility === 'selected' && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Select Members</label>
            {otherMembers.length === 0 ? (
              <p className="text-sm text-gray-500">No other band members to select.</p>
            ) : (
              <div className="space-y-1 max-h-40 overflow-y-auto border border-surface-700 rounded-lg p-2">
                {otherMembers.map((m: any) => (
                  <label key={m.userId} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(m.userId)}
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedUserIds([...selectedUserIds, m.userId]);
                        } else {
                          setSelectedUserIds(selectedUserIds.filter(id => id !== m.userId));
                        }
                      }}
                      className="rounded border-surface-600 bg-surface-800 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm">{m.name || m.email}</span>
                    <span className="text-xs text-gray-500 ml-auto">{m.role}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={createMutation.isPending || !title.trim() || (visibility === 'selected' && selectedUserIds.length === 0)}
          className="w-full bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg transition-colors text-sm"
        >
          {createMutation.isPending ? 'Creating...' : 'Create Note'}
        </button>
      </form>
    </Modal>
  );
}

function ManageEditorsModal({ open, onClose, note, bandId }: {
  open: boolean;
  onClose: () => void;
  note: any;
  bandId: number;
}) {
  const queryClient = useQueryClient();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(
    note.editors?.map((editor: any) => editor.userId) || []
  );

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['bandMembers', bandId],
    queryFn: () => api.bands.getMembers(bandId),
    enabled: open,
  });

  const updateEditors = useMutation({
    mutationFn: () => api.rehearsalNotes.updateEditors(note.id, selectedUserIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rehearsalNote', note.id] });
      queryClient.invalidateQueries({ queryKey: ['rehearsalNotes', bandId] });
      onClose();
    },
  });

  const eligibleMembers = members.filter((member: any) =>
    member.userId !== note.createdByUserId && member.role === 'member'
  );

  return (
    <Modal open={open} onClose={onClose} title="Manage Editors" maxWidth="max-w-lg">
      <form
        onSubmit={event => {
          event.preventDefault();
          updateEditors.mutate();
        }}
        className="space-y-4"
      >
        <div>
          <p className="text-sm text-gray-300">Choose band members who can edit this rehearsal note.</p>
          <p className="text-xs text-gray-500 mt-1">Editors cannot delete the note or manage other editors.</p>
        </div>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-gray-500">Loading members...</div>
        ) : eligibleMembers.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500 border border-surface-700 rounded-lg">
            No eligible band members.
          </div>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto overscroll-contain border border-surface-700 rounded-lg divide-y divide-surface-800">
            {eligibleMembers.map((member: any) => {
              const name = member.user?.displayName || member.user?.email || 'Band member';
              const checked = selectedUserIds.includes(member.userId);
              return (
                <label
                  key={member.userId}
                  className="min-h-14 flex items-center gap-3 px-3 py-2.5 hover:bg-surface-800 cursor-pointer"
                >
                  {member.user?.avatarUrl ? (
                    <img
                      src={member.user.avatarUrl}
                      alt=""
                      className="w-9 h-9 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-surface-700 flex items-center justify-center text-sm font-medium text-gray-300 shrink-0">
                      {name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-gray-200 truncate">{name}</span>
                    <span className="block text-xs text-gray-500">Band member</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={event => {
                      setSelectedUserIds(current =>
                        event.target.checked
                          ? [...current, member.userId]
                          : current.filter(id => id !== member.userId)
                      );
                    }}
                    className="w-5 h-5 rounded border-surface-600 bg-surface-800 text-primary-600 focus:ring-primary-500 shrink-0"
                  />
                </label>
              );
            })}
          </div>
        )}

        {updateEditors.isError && (
          <p className="text-sm text-red-400">
            {updateEditors.error instanceof Error ? updateEditors.error.message.replace(/^\d+:\s*/, '') : 'Unable to save editors.'}
          </p>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 px-4 text-sm text-gray-300 hover:bg-surface-800 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={updateEditors.isPending || isLoading}
            className="min-h-11 px-4 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors text-sm"
          >
            {updateEditors.isPending ? 'Saving...' : 'Save Editors'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditNoteModal({ open, onClose, note, bandId, canManageAccess }: {
  open: boolean;
  onClose: () => void;
  note: any;
  bandId: number;
  canManageAccess: boolean;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(note.title);
  const [visibility, setVisibility] = useState<Visibility>(note.visibility);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(
    note.permissions?.map((p: any) => p.userId) || []
  );

  const { data: members = [] } = useQuery({
    queryKey: ['bandMembers', bandId],
    queryFn: () => api.bands.getMembers(bandId),
    enabled: open,
  });

  const { user } = useAuth();

  const updateMutation = useMutation({
    mutationFn: () => api.rehearsalNotes.update(
      note.id,
      canManageAccess
        ? {
            title,
            visibility,
            selectedUserIds: visibility === 'selected' ? selectedUserIds : undefined,
          }
        : { title }
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rehearsalNotes', bandId] });
      queryClient.invalidateQueries({ queryKey: ['rehearsalNote', note.id] });
      onClose();
    },
  });

  const otherMembers = members.filter((m: any) => m.userId !== user?.id);

  return (
    <Modal open={open} onClose={onClose} title="Edit Rehearsal Note">
      <form onSubmit={e => { e.preventDefault(); updateMutation.mutate(); }} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        {canManageAccess && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Visibility</label>
            <div className="space-y-2">
              {(Object.entries(VISIBILITY_CONFIG) as [Visibility, typeof VISIBILITY_CONFIG.private][]).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <label
                    key={key}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      visibility === key
                        ? 'border-primary-500 bg-primary-600/10'
                        : 'border-surface-700 hover:border-surface-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="editVisibility"
                      value={key}
                      checked={visibility === key}
                      onChange={() => setVisibility(key)}
                      className="sr-only"
                    />
                    <Icon className={`w-4 h-4 ${config.color}`} />
                    <span className="text-sm">{config.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {canManageAccess && visibility === 'selected' && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Select Members</label>
            {otherMembers.length === 0 ? (
              <p className="text-sm text-gray-500">No other band members to select.</p>
            ) : (
              <div className="space-y-1 max-h-40 overflow-y-auto border border-surface-700 rounded-lg p-2">
                {otherMembers.map((m: any) => (
                  <label key={m.userId} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(m.userId)}
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedUserIds([...selectedUserIds, m.userId]);
                        } else {
                          setSelectedUserIds(selectedUserIds.filter(id => id !== m.userId));
                        }
                      }}
                      className="rounded border-surface-600 bg-surface-800 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm">{m.name || m.email}</span>
                    <span className="text-xs text-gray-500 ml-auto">{m.role}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={
            updateMutation.isPending
            || !title.trim()
            || (canManageAccess && visibility === 'selected' && selectedUserIds.length === 0)
          }
          className="w-full bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg transition-colors text-sm"
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </Modal>
  );
}
